import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { get } from 'idb-keyval';
import './Stepper.css';
import * as growthActions from '../../../redux/actions/growthEngine';
import * as billingActions from '../../../redux/actions/billing'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { del } from 'idb-keyval';
import { format } from "date-fns";
import { isAdAccountCampaignReady, getCampaignMinBudget, toScheduleDateString, isScheduleStartTimeTooSoon } from '../../../helper/utility'
import { PricingPlanModal } from '../../../components/PricingPlanModal';
import { CampaignFeePayModal, cleanupOrphanedStripeOverlays } from './CampaignFeePayModal';
import { canUseFeatureFmCampaignInsights } from '../../../helper/access';
import { CampaignCreatingLoader } from './CampaignCreatingLoader';
// Import pages
import { StepOne } from './StepOne';
import { StepTwo } from './StepTwo';
import { StepThree } from './StepThree';
import { StepFour } from './StepFour';
import { StepFive } from './StepFive';
import { StepSix } from './StepSix';
import { FeatureFM } from './FeatureFM';
import { TourLinkStep } from './TourLinkStep';
import { BioLinkStep } from './BioLinkStep';
import { DestinationUrlStep, shouldUseDestinationUrlStep, isValidDestinationUrl, normalizeDestinationUrl, getDestinationUrlError, FEATURE_FM_DESTINATION_OBJECTIVES } from './DestinationUrlStep';
import { YoutubeDestinationStep, isYoutubeDestinationSelected } from './YoutubeDestinationStep';
import { showToast } from '../../../helper/ShowToast';
import { useQueryClient } from '@tanstack/react-query';
import AppTour from '../../../components/AppTour/AppTour';
import { useMetaConnectionStatus } from '../../../queries/growthEngine/metaConnection.query';
import { formatUsdFee, useCampaignFeeQuote } from '../../../queries/growthEngine/campaignFee.query';

const resolveMetaSelectedPosts = (formData) => {
    const instagramPosts = formData.instagramMediaData || [];
    const facebookVideos = formData.facebookMediaData?.videos || [];
    const facebookPhotos = formData.facebookMediaData?.photos || [];

    return (formData.selectedPost || []).map((post) => {
        const livePost =
            instagramPosts.find((item) => item.id === post.id) ||
            facebookVideos.find((item) => item.id === post.id) ||
            facebookPhotos.find((item) => item.id === post.id) ||
            {};
        const videoUrl =
            post.url ||
            post.source ||
            post.media_url ||
            livePost.url ||
            livePost.source ||
            livePost.media_url ||
            null;

        return {
            ...livePost,
            ...post,
            id: post.id,
            type: post.type || livePost.type,
            platform: post.platform,
            url: videoUrl,
            source: post.source || livePost.source || videoUrl,
            thumbnail_url: post.thumbnail_url || livePost.thumbnail_url || null,
            media_url: post.media_url || livePost.media_url || videoUrl,
        };
    });
};

const steps = [
  {
    target: "[data-tour='campaign-selector']",
    content: "Use this section to select your campaign objective and the platform you want to run your campaign on. Your selections will determine the subsequent steps in the campaign creation process.",
    placement: "left",
    title: "Campaign Objective & Platform Selection"
  },
  {
    target: "[data-tour='next-steps']",
    content: "Continue to the next steps to set up your campaign details, audience targeting, budget, and schedule. Each step will guide you through the necessary information needed to create an effective campaign.",
    placement: "top",
    title: "Next Steps to Create Campaign"
  },
];

const Stepper = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [runTour, setRunTour] = useState(false)
    const [campaignSubmitPhase, setCampaignSubmitPhase] = useState(null);
    const processedAuthCodeRef = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const {hash} = useLocation()
    const [searchParams] = useSearchParams();
    const { currentStep, formData, artistTrackType, selectedRelease, selectedAlbumPlaylistTrack,
        artistSpotifyProfileUrl
    } = useSelector(state => state.growthEngine);
    const { artistInfo } = useSelector(state => state.artistDetails);
    const { hasActiveSubscription, subscriptionDetails } = useSelector((state) => state.billing);
    const access = useSelector((state) => state.auth.access);
    const authToken = useSelector((state) => state.auth.token);
    const userDetails = useSelector((state) => state.auth.userDetails);
    const metaCampaignUserKey =
        userDetails?._id ||
        userDetails?.id ||
        userDetails?.email ||
        userDetails?.assignedArtistSongstateId ||
        authToken ||
        null;
    const {
        data: metaConnectionStatus,
        isLoading: isMetaConnectionLoading,
    } = useMetaConnectionStatus({
        userKey: metaCampaignUserKey,
        enabled: !!authToken && !!metaCampaignUserKey,
    });
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [showCampaignFeePayModal, setShowCampaignFeePayModal] = useState(false);
    const hasFeatureFmAccess = canUseFeatureFmCampaignInsights(
        access,
        userDetails,
        { hasActiveSubscription }
    );
    const shouldShowFeatureFM = formData.shouldShowFeatureFM && hasFeatureFmAccess;
    const isMetaPlatform = formData.platformType === "Facebook" || formData.platformType === "Instagram";

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentStep]);

    useEffect(() => {
        if (isMetaConnectionLoading || !metaConnectionStatus) return;

        const hasStaleMetaUi =
            formData.metaLoggedIn ||
            formData.facebookUserId ||
            formData.pageList?.length ||
            formData.adAccountList?.length ||
            formData.adAccountId;

        if (metaConnectionStatus.needsReconnect || !metaConnectionStatus.connected) {
            if (hasStaleMetaUi) {
                dispatch(growthActions.clearMetaConnectionState());
            }
            return;
        }

        const hasValidMetaToken =
            metaConnectionStatus.connected &&
            (metaConnectionStatus.tokenStatus === "valid" ||
                metaConnectionStatus.tokenStatus === "expiring_soon");

        if (
            hasValidMetaToken &&
            (!formData.metaLoggedIn || !formData.facebookUserId)
        ) {
            dispatch(growthActions.getCurrentFacebookAccount(toast));
        }
    }, [
        metaConnectionStatus,
        isMetaConnectionLoading,
        formData.metaLoggedIn,
        formData.facebookUserId,
        formData.pageList?.length,
        formData.adAccountList?.length,
        formData.adAccountId,
        dispatch,
    ]);

    useEffect(() => {
        if(hash === "#start-tour"){
            setRunTour(true)
        }
    }, [hash])

    useEffect(() => {
        if (currentStep !== 0) {
            dispatch(growthActions.setStep(currentStep)); // Restore persisted step
        }
        // dispatch(growthActions.updateFormData({ isCampaignCreate: false }))
    }, [currentStep, dispatch]);
    useEffect(() => {
        if (!subscriptionDetails?.lastFetched) {
            dispatch(billingActions.getBillingDetails());
        }
    }, [dispatch, subscriptionDetails]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const errorCode = params.get("error_code") || params.get("error");
        const rawErrorMessage = params.get("error_message") || params.get("error_description");

        if (!errorCode && !rawErrorMessage) return;

        const decodedMessage = rawErrorMessage
            ? decodeURIComponent(rawErrorMessage.replace(/\+/g, " "))
            : "";

        let userMessage = decodedMessage || "Meta login was cancelled or failed. Please try again.";

        if (errorCode === "1349168" || /redirect.*uri/i.test(decodedMessage)) {
            userMessage =
                "Meta login failed: redirect URI is not allowed. Add http://localhost:3000/admin/growth-engine/campaign in Meta Developer → Facebook Login → Valid OAuth Redirect URIs, then open the app with http (not https).";
        } else if (errorCode === "access_denied" || /access denied/i.test(decodedMessage)) {
            userMessage = "Meta login was cancelled. You can try again when ready.";
        }

        showToast("error", userMessage);

        ["error_code", "error_message", "error", "error_description", "error_reason"].forEach((key) => {
            params.delete(key);
        });
        const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
        window.history.replaceState({}, "", newUrl);
    }, [searchParams]);

    useEffect(() => {
        const code = searchParams.get("code");
        if (
            code &&
            processedAuthCodeRef.current !== code &&
            !formData.metaLoggedIn &&
            !formData.facebookUserId &&
            (formData.platformType === "Facebook" || formData.platformType === "Instagram")
        ) {
            processedAuthCodeRef.current = code;
            dispatch(growthActions.updateFormData({ metaAuthPending: true }));
            const params = new URLSearchParams(window.location.search);
            params.delete("code");
            const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
            window.history.replaceState({}, "", newUrl);
            dispatch(growthActions.getAccessToken(code, toast, queryClient));
        } else if (code && processedAuthCodeRef.current !== code && formData.platformType === "YouTube") {
            processedAuthCodeRef.current = code;
            const params = new URLSearchParams(window.location.search);
            params.delete("code");
            const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
            window.history.replaceState({}, "", newUrl);
            dispatch(growthActions.getGoogleAccessToken(code, toast));
        }
    }, [searchParams, formData.metaLoggedIn, formData.facebookUserId, formData.platformType, dispatch, queryClient]);
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get("status");
        if (!status) return;
        setTimeout(() => {
            if (status === "success") showToast('success', "Success! Your subscription has been activated.")
            if (status === "cancel") showToast('error', "Payment cancelled")
        }, 50);
        // Remove status from URL WITHOUT reloading
        params.delete("status");
        const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
        window.history.replaceState({}, "", newUrl);
    }, []);

    const dynamicSteps = useMemo(() => {
        const steps = [
            { id: 'stepOne', component: <StepOne formData={formData} setFormData={data => dispatch(growthActions.updateFormData(data))} /> },
            { id: 'stepTwo', component: <StepTwo formData={formData} setFormData={data => dispatch(growthActions.updateFormData(data))} /> }
        ];
        if (shouldShowFeatureFM) {
            steps.push(
                { id: 'featureFM', component: <FeatureFM formData={formData} setFormData={data => dispatch(growthActions.updateFormData(data))} /> },
                { id: 'stepThree', component: <StepThree formData={formData} setFormData={data => dispatch(growthActions.updateFormData(data))} /> },
                { id: 'stepFour', component: <StepFour formData={formData} setFormData={data => dispatch(growthActions.updateFormData(data))} /> },
                { id: 'stepFive', component: <StepFive formData={formData} setFormData={data => dispatch(growthActions.updateFormData(data))} /> },
                { id: 'stepSix', component: <StepSix formData={formData} setFormData={data => dispatch(growthActions.updateFormData(data))} /> }
            );
            return steps;
        }

        const objective = formData.objective;
        const useDestinationUrl = shouldUseDestinationUrlStep(objective, hasFeatureFmAccess);

        if (objective === "Promote an Event" && hasFeatureFmAccess) {
            steps.push({
                id: 'tourLink',
                component: <TourLinkStep />
            });
        }

        if (objective === "Promote Bio Link" && hasFeatureFmAccess) {
            steps.push({
                id: 'bioLink',
                component: <BioLinkStep />
            });
        }

        if (useDestinationUrl) {
            steps.push({
                id: 'destinationUrl',
                component: <DestinationUrlStep />
            });
        }

        if (objective === "YouTube Growth") {
            steps.push({
                id: 'youtubeDestination',
                component: <YoutubeDestinationStep />
            });
        }

        const needsRestOfFlow =
            objective === "Instagram Growth" ||
            objective === "YouTube Growth" ||
            useDestinationUrl ||
            (objective === "Promote an Event" && hasFeatureFmAccess) ||
            (objective === "Promote Bio Link" && hasFeatureFmAccess);

        if (needsRestOfFlow) {
            steps.push(
                { id: 'stepThree', component: <StepThree formData={formData} setFormData={data => dispatch(growthActions.updateFormData(data))} /> },
                { id: 'stepFour', component: <StepFour formData={formData} setFormData={data => dispatch(growthActions.updateFormData(data))} /> },
                { id: 'stepFive', component: <StepFive formData={formData} setFormData={data => dispatch(growthActions.updateFormData(data))} /> },
                { id: 'stepSix', component: <StepSix formData={formData} setFormData={data => dispatch(growthActions.updateFormData(data))} /> }
            );
        }

        return steps;
    }, [formData, dispatch, shouldShowFeatureFM, hasFeatureFmAccess]);
    const pages = dynamicSteps.map(step => step.component);
    const totalSteps = pages.length;
    const isSummaryStep = dynamicSteps[currentStep]?.id === "stepSix";
    const showLimitedAccessCampaignFee = !hasActiveSubscription && isMetaPlatform && isSummaryStep;
    const campaignFeeQuery = useCampaignFeeQuote({
        adAccountId: formData.adAccountId,
        budget: formData.budget,
        budgetType: formData.budgetType,
        enabled: showLimitedAccessCampaignFee,
    });
    const lockedCampaignFee = campaignFeeQuery.data?.quote || null;

    useEffect(() => {
        return () => {
            cleanupOrphanedStripeOverlays();
        };
    }, []);

    useEffect(() => {
        if (!campaignSubmitPhase) {
            cleanupOrphanedStripeOverlays();
        }
    }, [campaignSubmitPhase]);

    useEffect(() => {
        if (dynamicSteps.length && currentStep >= dynamicSteps.length) {
            dispatch(growthActions.setStep(0));
        }
    }, [currentStep, dynamicSteps.length, dispatch]);

    const getLogicalStepIndex = (stepIndex) => {
        if (!shouldShowFeatureFM) return stepIndex;
        if (stepIndex <= 2) return stepIndex;
        if (stepIndex === 3) return 'featureFM';
        return stepIndex - 1;
    };


    const isInstagramGrowthReady = () => {
        if (formData.objective !== "Instagram Growth") return true;
        const page = formData.pageList?.find((item) => item.id === formData.pageId);
        const instagramId = formData.instagramId ? String(formData.instagramId) : "";
        const pageInstagramId = page?.instagram_id ? String(page.instagram_id) : "";
        if (!instagramId || !pageInstagramId || instagramId !== pageInstagramId) return false;
        const destinationUrl =
            formData.destinationUrl?.trim() ||
            page?.instagram_profile_url ||
            (page?.instagram_name ? `https://www.instagram.com/${page.instagram_name}` : "");
        return Boolean(destinationUrl);
    };

    const validateStep = (stepId) => {
        switch (stepId) {
            case 'stepOne':
                return formData.objective !== '';
            case 'stepTwo':
                if (formData.objective === "More Streams" || formData.objective === "Spotify Growth" || formData.objective === "Playlist Growth" || formData.objective === "Instagram Growth" ||
                    formData.objective === "Promote Custom Link" || formData.objective === "Promote an Event" || formData.objective === "Sell Your Merch" ||
                    formData.objective === "Promote Bio Link" || formData.objective === "YouTube Growth"
                ) {
                    return (
                        formData.platformType && (formData.metaLoggedIn || formData.facebookUserId) &&
                        formData.adAccountId && formData.pageId &&
                        isAdAccountCampaignReady(formData.adAccountList?.find(acc => acc.id === formData.adAccountId)).isReady &&
                        isInstagramGrowthReady()
                    );
                } else {
                    return false;
                }
            case 'youtubeDestination':
                return isYoutubeDestinationSelected(formData);
            case 'featureFM': {
                if (artistTrackType === 'artist') {
                    return artistSpotifyProfileUrl !== '';
                }
                if (!selectedRelease || Object.keys(selectedRelease).length === 0) return false;

                const type = selectedRelease.type; // Assuming your release object has `.type` like 'track', 'album', 'playlist'

                if (type === 'playlist' || type === 'album') {
                    return selectedAlbumPlaylistTrack && Object.keys(selectedAlbumPlaylistTrack).length > 0;
                }
                if (type === 'track') {
                    return true;
                }
                return false; // fallback for unknown type
            }
            case 'tourLink':
                return !!(formData.selectedTourLinkId && formData.destinationUrl);
            case 'bioLink':
                return !!(formData.selectedBioLinkId && formData.destinationUrl);
            case 'destinationUrl':
                return isValidDestinationUrl(normalizeDestinationUrl(formData.destinationUrl));
            case 'stepThree':
                if (isScheduleStartTimeTooSoon(formData.startDate, formData.startTime, formData.timezone)) {
                    return false;
                }
                if (formData.budgetType === 'lifetime') {
                    const start = new Date(`${format(formData.startDate, 'yyyy-MM-dd')}T${formData.startTime}`);
                    const end = new Date(`${format(formData.endDate, 'yyyy-MM-dd')}T${formData.endTime}`);
                    const diffInMs = end - start;
                    const diffInHours = diffInMs / (1000 * 60 * 60);
                    const isLessThan24Hours = diffInHours < 24;
                    const minBudget = getCampaignMinBudget({
                        budgetType: formData.budgetType,
                        minDailyBudget: formData.minDailyBudget,
                        startDate: formData.startDate,
                        startTime: formData.startTime,
                        endDate: formData.endDate,
                        endTime: formData.endTime,
                    });
                    const meetsMin = !formData.minDailyBudget || Number(formData.budget) >= minBudget;
                    return formData.budget && formData.startDate && formData.endDate && !isLessThan24Hours && meetsMin;
                } else {
                    const minBudget = getCampaignMinBudget({
                        budgetType: formData.budgetType,
                        minDailyBudget: formData.minDailyBudget,
                    });
                    const meetsMin = !formData.minDailyBudget || Number(formData.budget) >= minBudget;
                    return formData.budget && formData.startDate && meetsMin;
                }
            case 'stepFour':
                if (formData.objective === "More Streams" || formData.objective === "Spotify Growth" || formData.objective === "Playlist Growth" || formData.objective === "Instagram Growth" ||
                    formData.objective === "Promote Custom Link" || formData.objective === "Promote an Event" || formData.objective === "Playlist Growth" || formData.objective === "Sell Your Merch" ||
                    formData.objective === "Promote Bio Link" || formData.objective === "YouTube Growth"
                ) {
                    const metaTargeting = formData.targeting || {};
                    const hasValidLocation = formData?.selectedLocationOption === "global"
                        ? true
                        : !!(
                            (metaTargeting.location?.countries?.length > 0) ||
                            (metaTargeting.location?.regions?.length > 0) ||
                            (metaTargeting.location?.cities?.length > 0)
                        );

                    return (
                        formData.platformType &&
                        (formData.metaLoggedIn || formData.facebookUserId) &&
                        formData.adAccountId &&
                        formData.pageId &&
                        isAdAccountCampaignReady(formData.adAccountList?.find(acc => acc.id === formData.adAccountId)).isReady &&
                        isInstagramGrowthReady() &&
                        metaTargeting.gender &&
                        metaTargeting.ageRange?.min !== undefined &&
                        metaTargeting.ageRange?.max !== undefined &&
                        hasValidLocation &&
                        metaTargeting.languages?.length > 0 &&
                        metaTargeting.interests?.length > 0
                    );
                } else {
                    return false;
                }
            case 'stepFive':
                return formData.callToAction && formData.adCaption && formData.adHeadline &&
                    isValidDestinationUrl(normalizeDestinationUrl(formData.destinationUrl)) &&
                    (
                        (formData.selectMediaBy === 'meta' && formData.selectedPost?.length > 0) ||
                        (formData.selectMediaBy === 'upload' && JSON.parse(localStorage.getItem("uploadedMedia"))?.length > 0)
                    );
            case 'stepSix':
                return true;
            default:
                return false;
        }
    };
    const isFormDataValid = () => {
        const stepId = dynamicSteps[currentStep]?.id;
        return validateStep(stepId);
    };

    const processFeatureFM = async () => {
        if (artistTrackType === "artist") {
            return true; // no smartlink needed
        }

        let data = {
            songTitle: selectedRelease.name,
        };

        if (selectedRelease.type === "track") {
            data.trackId = selectedRelease.id;
            data.songImageUrl = selectedRelease.album.images[0].url;
        }

        if (selectedRelease.type === "album") {
            data.albumId = selectedRelease.id;
            data.songImageUrl = selectedRelease.images[0].url;
            data.autoplayTrackId = selectedAlbumPlaylistTrack.id;
            data.autoplayTrackSongName = selectedAlbumPlaylistTrack.name
        }

        if (selectedRelease.type === "playlist") {
            data.playlistId = selectedRelease.id;
            data.songImageUrl = selectedRelease.images[0].url;
            data.autoplayTrackId = selectedAlbumPlaylistTrack.id;
            data.autoplayTrackSongName = selectedAlbumPlaylistTrack.name
        }

        const response = await dispatch(
            growthActions.createSongSmartLink(data, setIsSubmitting)
        );
        if (!response.success) {
            toast.error("Failed to create Smart Link. Please try again.");
            return;
        }
        return response.success;
    };

    const handleNext = async () => {
        const stepId = dynamicSteps[currentStep]?.id;
        if (!validateStep(stepId)) {
            if (stepId === "destinationUrl" || (stepId === "stepFive" && getDestinationUrlError(formData.destinationUrl))) {
                showToast('warning', getDestinationUrlError(formData.destinationUrl));
            } else if (stepId === "youtubeDestination") {
                showToast('warning', "Please search and select a YouTube channel or video");
            } else if (stepId === "stepTwo" && formData.objective === "Instagram Growth" && !isInstagramGrowthReady()) {
                showToast('warning', "Please select an Instagram page");
            } else {
                showToast('warning', "Please fill required fields")
            }
            return;
        }
        if (stepId === "featureFM") {
            const success = await processFeatureFM();
            if (!success) return;
        }
        if (stepId === "destinationUrl" || stepId === "stepFive") {
            const normalized = normalizeDestinationUrl(formData.destinationUrl);
            if (normalized && normalized !== formData.destinationUrl) {
                dispatch(growthActions.updateFormData({ destinationUrl: normalized }));
            }
        }

        dispatch(growthActions.setStep(currentStep + 1));
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            dispatch(growthActions.setStep(currentStep - 1));
        }
    };

    const formatNumber = (num) => {
        if (num === 0) return "0";
        if (num < 1000) return num.toString();
        if (num >= 1_000 && num < 1_000_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + "K";
        if (num >= 1_000_000 && num < 1_000_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + "M";
        if (num >= 1_000_000_000 && num < 1_000_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + "B";
        if (num >= 1_000_000_000_000) return (num / 1_000_000_000_000).toFixed(1).replace(/\.0$/, '') + "T";
        return num.toString();
    };

    const handleCreateCampaign = async (feePayment = null) => {
        const paidFee = feePayment?.paymentIntentId ? feePayment : null;
        // Show loader
        // dispatch(growthActions.updateFormData({ isCampaignCreate: true }));
        try {
            //Check subscription for Meta-type campaigns only
            const metaObjectives = [
                "More Streams",
                "Spotify Growth",
                "Playlist Growth",
                "Instagram Growth",
                "Promote Custom Link",
                "Promote an Event",
                "Sell Your Merch",
                "Promote Bio Link",
                "YouTube Growth",
            ];

            // Limited Access Meta campaigns pay a campaign fee instead of requiring a subscription.
            if (metaObjectives.includes(formData.objective) && (formData.platformType === "Facebook" || formData.platformType === "Instagram")) {
                if (!hasActiveSubscription) {
                    if (!paidFee?.paymentIntentId) {
                        if (campaignFeeQuery.isLoading) {
                            showToast("warning", "Please wait while we calculate your campaign fee.");
                            return;
                        }
                        if (!lockedCampaignFee?.quoteId) {
                            showToast(
                                "warning",
                                campaignFeeQuery.error?.response?.data?.message ||
                                    "Unable to calculate the campaign fee. Please try again."
                            );
                            return;
                        }
                        setShowCampaignFeePayModal(true);
                        return;
                    }
                }
            }
            if (formData.objective === "More Streams" || formData.objective === "Spotify Growth" || formData.objective === "Playlist Growth" || formData.objective === "Instagram Growth" ||
                formData.objective === "Promote Custom Link" || formData.objective === "Promote an Event" || formData.objective === "Sell Your Merch" ||
                formData.objective === "Promote Bio Link" || formData.objective === "YouTube Growth"
            ) {
                const formDataToSend = new FormData();
                // Basic campaign data
                formDataToSend.append('name', formData.objective);
                formDataToSend.append('budget', formData.budget);
                formDataToSend.append('budgetType', formData.budgetType);
                formDataToSend.append('startDate', toScheduleDateString(formData.startDate));
                formDataToSend.append('endDate', formData.endDate ? toScheduleDateString(formData.endDate) : '');
                formDataToSend.append('startTime', formData.startTime);
                formDataToSend.append('endTime', formData.endTime);
                formDataToSend.append('adAccountId', formData.adAccountId);
                formDataToSend.append('facebookUserId', formData.facebookUserId);
                formDataToSend.append('pageId', formData.pageId);
                formDataToSend.append('instagramId', formData.instagramId);

                //put details selected relase based on objective
                if (!hasFeatureFmAccess && FEATURE_FM_DESTINATION_OBJECTIVES.includes(formData.objective)) {
                    const mapping = {
                        "More Streams": "Link Promotion",
                        "Spotify Growth": "Link Promotion",
                        "Playlist Growth": "Link Promotion",
                        "Promote an Event": "Sell Tickets",
                        "Promote Bio Link": "Bio Link Promotion",
                    };
                    const targetObject = {
                        targetType: "others",
                        targetName: mapping[formData.objective] || "Link Promotion",
                        targetImage: formData.selectedTargetImage || "",
                        targetTitle: formData.selectedTargetTitle || "",
                        linkId: formData.selectedTourLinkId || formData.selectedBioLinkId || "",
                        destinationUrlTrackId: formData.destinationUrlTrackId || "",
                    };
                    formDataToSend.append("targetObject", JSON.stringify(targetObject));
                } else if (formData.objective === "More Streams" || formData.objective === "Spotify Growth" || formData.objective === "Playlist Growth") {
                    if (artistTrackType === 'artist') {
                        let targetObject = {
                            targetType: 'spotifyProfile',
                            spotifyProfileUrl: artistSpotifyProfileUrl,
                            artistName: artistInfo?.name
                        }
                        formDataToSend.append('targetObject', JSON.stringify(targetObject))
                    } else {
                        let targetObject = {
                            targetType: 'song',
                            trackType: selectedRelease?.type,
                            trackName: selectedRelease?.name,
                            artistName: selectedRelease?.owner?.display_name || selectedRelease?.artists?.[0]?.name,
                            songImage: selectedRelease?.images?.[0]?.url || selectedRelease?.album?.images?.[0]?.url,
                            destinationUrlTrackId: formData.destinationUrlTrackId,
                            autoplayTrackId: selectedAlbumPlaylistTrack?.id || '',
                            autoplayTrackSongName: selectedAlbumPlaylistTrack?.name || '',
                        }
                        formDataToSend.append('targetObject', JSON.stringify(targetObject))
                    }
                    
                } else if (formData.objective === "Instagram Growth") {
                    //                      "Promote Custom Link"
                    // "Promote an Event"
                    // "Sell Your Merch"
                    let InstaInfo = formData.pageList.find(page => page.id === formData.pageId)
                    let targetObject = {
                        targetType: 'instaProfile',
                        artistName: InstaInfo?.instagram_display_name,
                        profilePicture: InstaInfo?.profile_picture,
                        instaId: InstaInfo?.instagram_id,
                        instaUserName: InstaInfo?.instagram_name
                    }
                    formDataToSend.append('targetObject', JSON.stringify(targetObject))
                } else if (["Promote Custom Link", "Promote an Event", "Sell Your Merch", "Promote Bio Link"].includes(formData.objective)) {

                    const mapping = {
                        "Promote Custom Link": "Link Promotion",
                        "Promote an Event": "Sell Tickets",
                        "Sell Your Merch": "Sell Merch",
                        "Promote Bio Link": "Bio Link Promotion"
                    };

                    const targetObject = {
                        targetType: "others",
                        targetName: mapping[formData.objective],
                        targetImage: formData.selectedTargetImage || "",
                        targetTitle: formData.selectedTargetTitle || "",
                        linkId: formData.selectedTourLinkId || formData.selectedBioLinkId || "",
                    };

                    formDataToSend.append("targetObject", JSON.stringify(targetObject));
                } else if (formData.objective === "YouTube Growth") {
                    const selectedYoutube = formData.selectedYoutubeContent || {};
                    const targetObject = {
                        targetType: "youtubeContent",
                        targetName: "YouTube Growth",
                        contentType: selectedYoutube.contentType || "",
                        targetTitle: selectedYoutube.title || formData.selectedTargetTitle || "",
                        targetImage: selectedYoutube.thumbnail || formData.selectedTargetImage || "",
                        youtubeUrl: selectedYoutube.youtubeUrl || formData.destinationUrl || "",
                        youtubeId: selectedYoutube.youtubeId || "",
                        channelTitle: selectedYoutube.channelName || "",
                        artistName: selectedYoutube.contentType === "video" ? (selectedYoutube.channelName || "") : "",
                        handle: selectedYoutube.handle || "",
                        sourceMode: selectedYoutube.sourceMode || formData.youtubeDestinationMode || "",
                        ownerChannelId: selectedYoutube.ownerChannelId || "",
                    };
                    formDataToSend.append("targetObject", JSON.stringify(targetObject));
                }
                // Targeting - stringify since backend checks for JSON string
                formDataToSend.append('targeting', JSON.stringify({
                    ageRange: {
                        min: formData.targeting.ageRange.min,
                        max: formData.targeting.ageRange.max
                    },
                    location: {
                        countries: formData.targeting.location.countries.map(c => ({
                            id: c.id || c.code,
                            name: c.name
                        })),
                        regions: formData.targeting.location.regions.map(r => ({
                            id: r.id,
                            name: r.name
                        })),
                        cities: formData.targeting.location.cities.map(c => ({
                            id: c.id,
                            name: c.name
                        })),
                        locationEnum: formData.targeting.location.locationEnum
                    },
                    gender: formData.targeting.gender,
                    interests: formData.targeting.interests.map(i => ({
                        id: i.id,
                        name: i.name
                    })),
                    behaviors: formData.targeting.behaviors.map(b => ({
                        id: b.id,
                        name: b.name
                    })),
                    languages: formData.targeting.languages.map(l => ({
                        id: l.id,
                        name: l.name
                    })),
                    deviceTypes: formData.targeting.deviceTypes
                }));

                // You can add more creative fields as needed:
                formDataToSend.append('selectMediaBy', formData.selectMediaBy);
                formDataToSend.append('callToAction', formData.callToAction);
                formDataToSend.append('adCaption', formData.adCaption);
                formDataToSend.append('destinationUrl', formData.destinationUrl);
                formDataToSend.append('destinationUrlTrackId', formData.destinationUrlTrackId); // only for targettype song
                formDataToSend.append('adHeadline', formData.adHeadline);
                formDataToSend.append('adDescription', formData.adDescription || '');
                if (paidFee?.quoteId && paidFee?.paymentIntentId) {
                    formDataToSend.append('campaignFeeQuoteId', paidFee.quoteId);
                    formDataToSend.append('campaignFeePaymentIntentId', paidFee.paymentIntentId);
                }

                if (formData.selectMediaBy === 'upload') {
                    const uploadedMedia = await get("uploadedFiles");
                    const mediaTypesArray = [];
                    uploadedMedia.forEach((file) => {
                        if (file && file.type) {
                            const type = file.type.startsWith("video") ? "Video" : "Image";
                            formDataToSend.append("mediaFiles", file);
                            mediaTypesArray.push(type);
                        }
                    });
                    mediaTypesArray.forEach(type => {
                        formDataToSend.append("mediaTypes", type);
                    });
                } else if (formData.selectMediaBy === 'meta') {
                    const selectedPosts = resolveMetaSelectedPosts(formData);
                    console.log('[createCampaign] selectedPost payload', selectedPosts.map((post) => ({
                        id: post.id,
                        type: post.type,
                        platform: post.platform,
                        hasUrl: Boolean(post.url),
                        hasSource: Boolean(post.source),
                        urlPreview: post.url ? String(post.url).substring(0, 120) : post.url,
                        sourcePreview: post.source ? String(post.source).substring(0, 120) : post.source,
                    })));
                    formDataToSend.append("selectedPost", JSON.stringify(selectedPosts));
                }
                setCampaignSubmitPhase('creating');
                dispatch(growthActions.createCampaign(formDataToSend, toast, navigate, queryClient, {
                    onSuccess: () => setCampaignSubmitPhase('success'),
                    onError: () => {
                        setCampaignSubmitPhase(null);
                        cleanupOrphanedStripeOverlays();
                    },
                }));
            }
        } finally {
            // campaignSubmitPhase cleared via createCampaign callbacks
        }
    }

    const handleCancel = async () => {
        dispatch(growthActions.cancelFormDataState());
        // Clear uploaded files from IndexedDB and localstorage
        await del("uploadedFiles");
        localStorage.removeItem("uploadedMedia");
        // Redirect to /admin/growth-engine
        navigate("/admin/growth-engine");
    };

    return (
        <div className="stepper-page relative">
            <ToastContainer />
            <AppTour
                tourSteps={steps}
                runTour={runTour}
                setRunTour={setRunTour}
            />
            <div className="container max-w-full p-0">
                <div className="bg-theme_clr1 px-4 py-6 lg:p-10">
                    <div className='btn-cancel text-right mb-5' onClick={handleCancel}><i className="bi bi-x-square-fill text-[22px] text-white cursor-pointer"></i></div>
                    <div className="custom-wrapper max-w-[875px] mx-auto mb-[23.5px]" data-tour="campaign-selector">
                        {/* Stepper Progress Bar */}
                        <div className="progressbar-navigation text-center mb-[21px]">
                            <div className="relative max-w-[600px] w-full inline-flex items-center justify-between">
                                {dynamicSteps.map((_, index) => (
                                    <div key={index} className={`relative w-[30px] md:w-[36px] h-[30px] md:h-[36px] text-white text-sm md:text-base font-medium flex items-center justify-center border rounded-full transition-all z-10 ${index <= currentStep ? 'bg-theme_clr2 border-theme_clr2' : 'bg-theme_clr1 border-white'}`}>{index + 1}</div>
                                ))}

                                {/* Connecting Bars */}
                                <div className={`absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-[1px] bg-gray-300 z-0`}></div>

                                {/* Highlighted Active Line */}
                                <div className={`absolute left-0 top-1/2 transform -translate-y-1/2 h-[2px] ${currentStep === totalSteps ? "bg-theme_clr2" : "bg-theme_clr2"}`}
                                    style={{ width: `calc(${(currentStep / (totalSteps - 1)) * 100}%)` }}
                                // Adjust to handle step completion accurately
                                >
                                </div>
                            </div>
                        </div>

                        {/* Page Content */}
                        <div className="content-wrapper">{pages[currentStep]}</div>
                    </div>

                    {/* Buttons */}
                    <div data-tour="next-steps">
                    {
                        currentStep !== pages.length - 1 ?
                            <div className="bg-white flex flex-wrap items-center justify-between gap-2 rounded-2xl p-4 xl:p-[18px]">
                                {
                                    dynamicSteps[currentStep]?.id === 'stepFour' && formData.platformType !== "YouTube" ?
                                        <div className="left-sec text-theme_clr1 font-inter font-medium">
                                            <div className="text-xs mb-1">Potential reach</div>
                                            <div className="text-[14.5px]">{formatNumber(formData.potentialReach?.min)} - {formatNumber(formData.potentialReach?.max)}</div>
                                        </div> :
                                        <div className="left-sec text-theme_clr1 font-inter font-medium"></div>
                                }

                                <div className="right-sec flex flex-wrap gap-3 lg:gap-4">
                                    <button className="bg-transparent text-theme_clr2 text-[12.3px] font-inter font-medium tracking-[0.037] border border-theme_clr2 rounded-lg disabled:opacity-75 py-[10px] px-4"
                                        onClick={handlePrevious}
                                        disabled={currentStep === 0}
                                    >
                                        <i className="bi bi-chevron-left"></i> Back
                                    </button>
                                    {/* <button className="bg-theme_clr2 text-white text-[12.3px] font-inter font-medium tracking-[0.037] rounded-lg disabled:opacity-75 py-[10px] px-4"
                                        onClick={handleNext}
                                        disabled={currentStep === pages.length - 1 || !isFormDataValid(formData, currentStep)}
                                    >
                                        Continue <i className="bi bi-chevron-right"></i>
                                    </button> */}
                                    <button
                                        className="bg-theme_clr2 text-white text-[12.3px] font-inter font-medium tracking-[0.037] rounded-lg disabled:opacity-75 py-[10px] px-4 flex items-center gap-2"
                                        onClick={handleNext}
                                        disabled={isSubmitting || currentStep === pages.length - 1 || !isFormDataValid(formData, currentStep)}
                                    >
                                        {isSubmitting && <i className="bi bi-arrow-repeat animate-spin"></i>}
                                        Continue <i className="bi bi-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                            : ''}

                    {/* Buttons */}
                    {
                        currentStep === pages.length - 1 ?
                            <div className="bg-theme_clr2 flex flex-wrap items-center justify-between gap-2 rounded-2xl p-4 xl:p-[18px]">

                                <div className="left-sec text-theme_clr1 font-inter font-medium leading-none">
                                    {
                                        formData.platformType === 'YouTube' ?
                                            <div className="text-[12.8px]">
                                                Your marketing campaign may take up to 48 hours to go live. You will be redirected to a payment page to finish checkout.
                                            </div>
                                            :
                                            showLimitedAccessCampaignFee ? (
                                                <>
                                                    <div className="text-[12.8px]">Due to UpNComer today</div>
                                                    <div className="text-base">
                                                        {campaignFeeQuery.isLoading
                                                            ? "…"
                                                            : formatUsdFee(lockedCampaignFee?.finalCampaignFeeUSD)}
                                                    </div>
                                                    <div className="text-[12.8px]">
                                                        Campaign fee based on your {lockedCampaignFee?.campaignCurrency || formData.currency || "ad account"} campaign budget.
                                                    </div>
                                                </>
                                            ) : (
                                            <>
                                                {/* <div className="text-[12.8px]">Total to pay now</div>
                                                <div className="text-base">$0.00</div>
                                                <div className="text-[12.8px]">UpNComer service fee</div> */}
                                            </>
                                            )
                                    }
                                    {/* <div className="text-[12.8px]">Total to pay now</div>
                                                <div className="text-base">$0.00</div>
                                                <div className="text-[12.8px]">UpNComer service fee</div> */}

                                </div>

                                <div className="right-sec flex flex-wrap gap-3 lg:gap-4">
                                    <button className="bg-theme_clr1 text-white text-xs font-inter font-medium tracking-[0.037] border border-white rounded-lg py-[10px] px-4"
                                        onClick={handlePrevious}>
                                        <i className="bi bi-chevron-left"></i> Back
                                    </button>
                                    <button
                                        className="bg-white text-theme_clr1 text-xs font-inter font-medium tracking-[0.037] rounded-lg py-[10px] px-4 flex items-center gap-2 disabled:opacity-60"
                                        onClick={handleCreateCampaign}
                                        disabled={formData.isCampaignCreate || !!campaignSubmitPhase}
                                    >
                                        Confirm and pay
                                    </button>
                                </div>
                            </div>
                            : ''
                    }
                    </div>

                </div>
                {campaignSubmitPhase && (
                    <CampaignCreatingLoader phase={campaignSubmitPhase} />
                )}
            </div>
            <PricingPlanModal
                isOpen={showPricingPlanModal}
                onClose={() => setShowPricingPlanModal(false)}
                returnUrl={window.location.origin + "/admin/growth-engine/campaign"}
            />
            <CampaignFeePayModal
                isOpen={showCampaignFeePayModal}
                quote={lockedCampaignFee}
                onClose={() => {
                    setShowCampaignFeePayModal(false);
                    cleanupOrphanedStripeOverlays();
                }}
                onPaid={(paymentIntentId) => {
                    const quoteId = lockedCampaignFee?.quoteId;
                    setShowCampaignFeePayModal(false);
                    cleanupOrphanedStripeOverlays();
                    window.setTimeout(() => {
                        cleanupOrphanedStripeOverlays();
                        handleCreateCampaign({
                            quoteId,
                            paymentIntentId,
                        });
                    }, 80);
                }}
            />
        </div>
    );
};

export default Stepper;
